import { v4 } from "uuid";

import { ComponentDto } from "@intelligems/ig-types/dtos/components";
import { isOnsiteUpsellComponent } from "@intelligems/ig-utils/onsite-upsell-component";
import * as rtk from "@reduxjs/toolkit";
import { createSelector, type PayloadAction } from "@reduxjs/toolkit";

import { IExperienceComponentsState } from "../types/experienceComponents";

// Holds the experience's components — the server source of truth, mirroring the
// onsiteEdits / onsiteInjections slices. Typed to the general `ComponentDto`
// (onsiteUpsell today, bundle soon); onsiteUpsell-specific handling narrows at
// the conversion boundary. `storedComponents` is the loaded baseline;
// `components` is what the next save sends. The visual builder converts these
// to/from mods; a save with the builder closed still sends the stored set, so
// an unrelated save doesn't wipe persisted components.
const initialState: IExperienceComponentsState = {
  components: [],
  storedComponents: [],
  reportedCreatedOnsiteUpsellIds: [],
};

const experienceComponents = rtk.createSlice({
  name: "experienceComponents",
  initialState,
  reducers: {
    setComponents: (draft, action: PayloadAction<ComponentDto[]>) => {
      draft.components = action.payload;
    },
    initComponents: (draft, action: PayloadAction<ComponentDto[]>) => {
      draft.components = action.payload;
      draft.storedComponents = action.payload;
      draft.reportedCreatedOnsiteUpsellIds = [];
    },
    markCreatedOnsiteUpsellsReported: (
      draft,
      action: PayloadAction<string[]>
    ) => {
      draft.reportedCreatedOnsiteUpsellIds = [
        ...new Set([
          ...draft.reportedCreatedOnsiteUpsellIds,
          ...action.payload,
        ]),
      ];
    },
    resetComponents: () => initialState,
    // Replace a variation's components with a copied set, for copy-to-group.
    // Mirrors `setOnsiteEditsByVariationId`: the copies get fresh ids.
    setComponentsByVariationId: (
      draft,
      action: PayloadAction<{
        components: ComponentDto[];
        variationId: string;
      }>
    ) => {
      const others = draft.components.filter(
        (component) => component.variationId !== action.payload.variationId
      );
      const copied = action.payload.components.map((component) => ({
        ...component,
        variationId: action.payload.variationId,
        id: v4(),
      }));
      draft.components = [...others, ...copied];
    },
    // Drop a variation's components when its upsell mod is cleared.
    resetComponentsByVariationId: (
      draft,
      action: PayloadAction<{ variationId: string }>
    ) => {
      draft.components = draft.components.filter(
        (component) => component.variationId !== action.payload.variationId
      );
    },
  },
  selectors: {
    selectedComponents: (state) => state.components,
    selectedStoredComponents: (state) => state.storedComponents,
    selectedReportedCreatedOnsiteUpsellIds: (state) =>
      state.reportedCreatedOnsiteUpsellIds,
  },
});

export const {
  setComponents,
  initComponents,
  markCreatedOnsiteUpsellsReported,
  resetComponents,
  setComponentsByVariationId,
  resetComponentsByVariationId,
} = experienceComponents.actions;

export const {
  selectedComponents,
  selectedStoredComponents,
  selectedReportedCreatedOnsiteUpsellIds,
} = experienceComponents.selectors;

export const experienceComponentsReducer = experienceComponents.reducer;

export const getVariationSpecificComponents = (variationId: string) =>
  createSelector(selectedComponents, (components) =>
    components.filter((component) => component.variationId === variationId)
  );

// The onsite-upsell components (list and bundle), optionally scoped to one
// variation.
export const getOnsiteUpsellComponents = (variationId?: string) =>
  createSelector(selectedComponents, (components) =>
    components
      .filter(isOnsiteUpsellComponent)
      .filter(
        (component) => !variationId || component.variationId === variationId
      )
  );

// Onsite-upsell components created this session — absent from the loaded
// baseline. Lets creation tracking fire only for genuinely new upsells;
// pre-existing ones are edits.
export const getNewOnsiteUpsellComponents = () =>
  createSelector(
    getOnsiteUpsellComponents(),
    selectedStoredComponents,
    (upsells, storedComponents) => {
      const storedIds = new Set(storedComponents.map((c) => c.id));
      return upsells.filter((component) => !storedIds.has(component.id));
    }
  );

export const getOnsiteUpsellComponentIds = createSelector(
  selectedComponents,
  (components) =>
    components.filter(isOnsiteUpsellComponent).map((component) => component.id)
);

export const getHasAnyOnsiteUpsellComponents = () =>
  createSelector(getOnsiteUpsellComponentIds, (ids) => ids.length > 0);
